import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCard, MatCardContent, MatCardTitle } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { TablerIconsModule } from 'angular-tabler-icons';
import { firstValueFrom } from 'rxjs';
import { leadServices } from 'src/app/services/leads.service';
import { ItemsDublicateComponent } from './items-dublicate/items-dublicate.component';
import { DialogComponent } from '../dialog/confirm.dialog';

@Component({
  selector: 'app-dublicated-lead',
  standalone: true,
  imports: [
    CommonModule,
    MatDivider,
    TablerIconsModule,
    MatCardContent,
    MatCard,
    MatButtonModule,
    MatIcon,
    MatCardTitle,
    FormsModule,
    MatCheckbox,
    ItemsDublicateComponent,
  ],
  templateUrl: './dublicated-lead.component.html',
  styleUrl: './dublicated-lead.component.scss',
})
export class DublicatedLeadComponent implements OnInit {
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<DublicatedLeadComponent>,
    private leadService: leadServices,
    private dialog: MatDialog,
  ) {}

  duplicatedLeads: any[] = [];
  previewDetails: any;

  ngOnInit(): void {
    this.getData();
  }

  async getData() {
    this.duplicatedLeads = await firstValueFrom(
      this.leadService.getDuplicate(
        this.data.email,
        this.data.phoneNumber,
        this.data.prefix,
        this.data.name,
        this.data.lastName,
      ),
    );
    if (this.duplicatedLeads.length > 0) {
      this.duplicatedLeads[0].isPrimary = true;
    }
  }

  updatedPrimary(event: any) {
    this.duplicatedLeads = event;
  }

  async keepSeperate() {
    const header = 'Resolve Merge Conflict Separately?';
    const content =
      'Do you want to resolve the merge conflict by keeping the leads separate?';
    const confirm = await this.callDialog(header, content);

    if (confirm) {
      const data = {
        duplicateFilter: {
          email: this.data.email,
          phoneNumber: this.data.phoneNumber,
          prefix: this.data.prefix,
          name: this.data.name,
          lastName: this.data.lastName,
        },
      };

      this.resolveDuplicate(false, data);
    }
  }

  closeCard() {
    this.dialogRef.close({
      success: false,
    });
  }

  async merge() {
    if (!this.previewDetails) {
      console.error('No preview details available to merge.');
      return;
    }

    // ── Document conflict check ──────────────────────────────────────────
    const docConflict = this.detectDocumentConflicts();

    if (docConflict.hasSameDocuments) {
      const confirmed = await this.callDialog(
        'Duplicate Documents Detected',
        "Some leads share the same documents. The primary lead's version will be kept and the duplicates will be discarded. Do you want to continue?",
      );
      if (!confirmed) return;
    } else if (docConflict.hasDifferentDocuments) {
      const confirmed = await this.callDialog(
        'Documents Will Be Merged',
        'The selected leads have different documents. All documents will be merged into the primary lead. Do you want to continue?',
      );
      if (!confirmed) return;
    }

    // ── Application conflict check ───────────────────────────────────────
    const appConflict = this.detectApplicationConflicts();

    if (appConflict.hasSameApplications) {
      const confirmed = await this.callDialog(
        'Duplicate Applications Detected',
        "Some leads have submitted the same application. The primary lead's application data will be kept and duplicates will be discarded. Do you want to continue?",
      );
      if (!confirmed) return;
    } else if (appConflict.hasDifferentApplications) {
      const confirmed = await this.callDialog(
        'Applications Will Be Merged',
        'The selected leads have different applications. All application data will be merged into the primary lead. Do you want to continue?',
      );
      if (!confirmed) return;
    }

    // ── Final merge confirmation (existing) ─────────────────────────────
    const header = 'Merge into Primary Lead?';
    const content =
      'Do you want to merge duplicate lead profiles into one primary lead?';
    const confirm = await this.callDialog(header, content);

    if (!confirm) return;

    const primaryLeadId = this.previewDetails.id;

    const mergeData = {
      primaryLeadId,
      duplicateFilter: {
        email: this.data.email,
        phoneNumber: this.data.phoneNumber,
        prefix: this.data.prefix,
        name: this.data.name,
        lastName: this.data.lastName,
      },
    };

    this.resolveDuplicate(true, mergeData);
  }

  cancel() {
    delete this.previewDetails;
  }

  preview() {
    if (!this.duplicatedLeads.length) return;

    const primaryLead = this.duplicatedLeads.find((lead) => lead.isPrimary);

    if (!primaryLead) {
      alert('Please select a primary lead to preview.');
      return;
    }

// Notes, activities, messages: sum all (all get merged in)
const aggregatedCounts = this.duplicatedLeads.reduce(
  (acc, lead) => {
    acc.notes += lead._count?.note || 0;
    acc.activities += lead._count?.tasks || 0;
    acc.messages += lead._count?.messages || 0;
    return acc;
  },
  { notes: 0, activities: 0, messages: 0 },
);

// Documents: union of unique document_ids across all leads
const allDocumentIds = new Set<number>();
for (const lead of this.duplicatedLeads) {
  for (const doc of lead.lead_documents || []) {
    allDocumentIds.add(doc.document_id);
  }
}

// Applications: union of unique application_ids across all leads
const allApplicationIds = new Set<number>();
for (const lead of this.duplicatedLeads) {
  for (const app of lead.lead_applications || []) {
    allApplicationIds.add(app.application_id);
  }
}

this.previewDetails = {
  ...primaryLead,
  _count: {
    ...primaryLead._count,
    note: aggregatedCounts.notes,
    tasks: aggregatedCounts.activities,
    messages: aggregatedCounts.messages,
    lead_documents: allDocumentIds.size,
    lead_applications: allApplicationIds.size,
  },
};

  }

  async resolveDuplicate(isMerged: boolean, mergeData: any) {
    const response: any = await firstValueFrom(
      this.leadService.resolveDuplicate(mergeData, isMerged),
    );

    if (response && isMerged) {
      this.dialogRef.close({
        success: true,
        id: mergeData.primaryLeadId,
        isMerged: true,
      });
    } else if (response && !isMerged) {
      this.dialogRef.close({
        success: true,
        isMerged: false,
      });
    } else {
      this.dialogRef.close({
        success: false,
      });
    }
  }

  // ── Document conflict detection ──────────────────────────────────────────

  private detectDocumentConflicts(): {
    hasDifferentDocuments: boolean;
    hasSameDocuments: boolean;
  } {
    const empty = { hasDifferentDocuments: false, hasSameDocuments: false };

    if (!Array.isArray(this.duplicatedLeads) || this.duplicatedLeads.length < 2) {
      return empty;
    }

    const primary = this.duplicatedLeads.find((l) => l.isPrimary);
    if (!primary) return empty;

    const primaryDocIds = new Set(
      (primary.lead_documents || []).map((d: any) => d.document_id),
    );

    let hasDifferentDocuments = false;
    let hasSameDocuments = false;

    for (const lead of this.duplicatedLeads.filter((l) => !l.isPrimary)) {
      for (const doc of lead.lead_documents || []) {
        if (primaryDocIds.has(doc.document_id)) {
          hasSameDocuments = true;
        } else {
          hasDifferentDocuments = true;
        }
      }
    }

    return { hasDifferentDocuments, hasSameDocuments };
  }

  // ── Application conflict detection ──────────────────────────────────────

  private detectApplicationConflicts(): {
    hasDifferentApplications: boolean;
    hasSameApplications: boolean;
  } {
    const empty = { hasDifferentApplications: false, hasSameApplications: false };

    if (!Array.isArray(this.duplicatedLeads) || this.duplicatedLeads.length < 2) {
      return empty;
    }

    const primary = this.duplicatedLeads.find((l) => l.isPrimary);
    if (!primary) return empty;

    const primaryAppIds = new Set(
      (primary.lead_applications || []).map((a: any) => a.application_id),
    );

    let hasDifferentApplications = false;
    let hasSameApplications = false;

    for (const lead of this.duplicatedLeads.filter((l) => !l.isPrimary)) {
      for (const app of lead.lead_applications || []) {
        if (primaryAppIds.has(app.application_id)) {
          hasSameApplications = true;
        } else {
          hasDifferentApplications = true;
        }
      }
    }

    return { hasDifferentApplications, hasSameApplications };
  }

  // ── Dialog helper ────────────────────────────────────────────────────────

  callDialog(header: string, content: string, width = '500px', isInfo = false) {
    const dialogRef = this.dialog.open(DialogComponent, {
      data: {
        header,
        content,
        isInfo,
        type: 'confirm',
      },
      width,
    });

    return dialogRef.afterClosed().toPromise();
  }
}